import type {
  BlocoDocumento,
  ContextoPaginaDocumento,
} from '@/shared/components/documentos/paginado';

import type {
  MensagemWhatsappCertificado,
  RespostaWhatsappCertificado,
} from '../../../types/certificadoAcessosTypes';
import { agruparConversasWhatsapp } from '../../../utils/conversasWhatsapp';
import {
  CabecalhoConversaWhatsapp,
  FatiaConversaWhatsapp,
  FatiaEvidenciaConversa,
} from '../secoes/SecaoTextosEnviados';

/**
 * A réplica de WhatsApp entra na paginação como uma conversa por número, e cada conversa como um
 * bloco por linha de evidência e um bloco por bolha — nunca como moldura atômica.
 *
 * Com uma bolha por moldura, a réplica nunca passava da folha. Juntas numa conversa, as bolhas
 * de uma citação longa passam de 971px, e `distribuirBlocosEmPaginas` empurra bloco indivisível
 * inteiro: o excedente seria cortado pelo `overflow-hidden` da página e coberto pelo rodapé, em
 * silêncio — o mesmo defeito que `blocosReplicaEmail` e `blocosAuditoriaDestinatario` resolvem.
 *
 * O cabeçalho da conversa fica preso à primeira linha da tabela, e a tabela à primeira bolha:
 * sem isso, o nome da parte podia terminar uma página e a conversa dela começar na seguinte.
 *
 * Vive fora do componente de documento porque os DOIS certificados de acessos (o do processo
 * arbitral e o da sentença) montam esta mesma réplica. Só o do processo tem as respostas da parte.
 */
export function blocosConversasWhatsapp(
  mensagens: MensagemWhatsappCertificado[],
  idBase: string,
  respostas: RespostaWhatsappCertificado[] = [],
  /** "De:" da conversa que só tem respostas da parte, sem mensagem da câmara de onde tirá-lo. */
  remetente = '',
): BlocoDocumento[] {
  return agruparConversasWhatsapp(mensagens, respostas, remetente).flatMap(
    (conversa, indiceConversa) => {
      const id = `${idBase}-${indiceConversa}`;
      const { bolhas } = conversa;
      const ultimoIndice = bolhas.length - 1;

      const linhasEvidencia = bolhas.map(
        (bolha, indice): BlocoDocumento => ({
          id: `${id}-evidencia-${indice}`,
          manterComProximo: indice === ultimoIndice,
          render: (contexto: ContextoPaginaDocumento) => (
            <FatiaEvidenciaConversa
              bolha={bolha}
              primeira={indice === 0}
              ultima={indice === ultimoIndice}
              continuacao={indice > 0 && contexto.primeiroDaPagina}
            />
          ),
        }),
      );

      const fatiasConversa = bolhas.map(
        (bolha, indice): BlocoDocumento => ({
          id: `${id}-bolha-${indice}`,
          render: (contexto: ContextoPaginaDocumento) => (
            <FatiaConversaWhatsapp
              conversa={conversa}
              bolha={bolha}
              primeira={indice === 0}
              ultima={indice === ultimoIndice}
              continuacao={indice > 0 && contexto.primeiroDaPagina}
              mostrarData={indice === 0 || bolhas[indice - 1].dataLabel !== bolha.dataLabel}
            />
          ),
        }),
      );

      return [
        {
          id: `${id}-cabecalho`,
          manterComProximo: true,
          node: <CabecalhoConversaWhatsapp conversa={conversa} />,
        },
        ...linhasEvidencia,
        ...fatiasConversa,
      ];
    },
  );
}
